/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import filtersIcon from '../../../../assets/icons/filters-icon.svg';
import { filtersBlockStyles } from './filters-block.styles';
import { filtersValues } from './constants';
import QuickFilterItem from './quick-filter-item/quick-filter-item';
import FilterMenu from './filter-menu/filter-menu.component';
import { useDispatch, useSelector } from 'react-redux';
import { selectLocationsParams } from '../../../../store/locations/locations-list.selector';
import {
    ArrayParam,
    BooleanParam,
    NumberParam,
    StringParam,
    useQueryParams,
    withDefault,
} from 'use-query-params';
import {
    setLocationsParamsCanadianMadeOnly,
    setLocationsParamsConnectorStatus,
    setLocationsParamsConnectorTypes,
    setLocationsParamsIsFree,
    setLocationsParamsPower,
    setLocationsParamsSearch,
} from '../../../../store/locations/locations-list.slice';
import { EStationStatus } from '../../../../api/station/types';
import { useLocations } from '../../../../hooks/useLocations';
import { EConnectorType, getConnectorTypes, normalizeConnectorType } from '../../../set-up/utils/getConnectorTypes';
import { validatePower } from './power/power.constants';
import { useIntl } from 'react-intl';

const ConnectorsFiltersParam = withDefault(ArrayParam, []);
const availableConnectorTypes = new Set(Object.keys(getConnectorTypes()));

const FiltersBlock = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dispatch = useDispatch();
    const { setLocationsFilterSearch } = useLocations();
    const intl = useIntl();

    const searchFilters = useSelector(selectLocationsParams);

    const [query, setQuery] = useQueryParams({
        search: StringParam,
        isFree: BooleanParam,
        maxPower: NumberParam,
        minPower: NumberParam,
        canadianMadeOnly: BooleanParam,
        connectorStatus: StringParam,
        connectors: ConnectorsFiltersParam,
    });

    const clickFilterHandler = useCallback(
        (reducer: any) => (value: any) => {
            dispatch(reducer(value));
        },
        []
    );

    const quickFilterClickHandler = useCallback(
        (reducer: any, params: string) => (value: any) => {
            dispatch(reducer(value));
            setQuery({ [params]: value });
        },
        []
    );

    const quickFilters = useMemo(() => {
        return filtersValues.map(filter => {
            const value = searchFilters[filter.params as keyof typeof searchFilters] as boolean;
            return (
                <QuickFilterItem
                    key={filter.id}
                    // name={filter.label}
                    name={intl.formatMessage({ id: filter.label })}
                    value={value}
                    onClick={quickFilterClickHandler(filter.reducer, filter.params)}
                />
            );
        });
    }, [searchFilters]);

    useEffect(() => {
        if (query.connectorStatus) {
            dispatch(setLocationsParamsConnectorStatus(query.connectorStatus as EStationStatus));
        }
        if (query.connectors.length > 0) {
            const normalizedConnectors = query.connectors
                .map(type => normalizeConnectorType(type))
                .filter(type => availableConnectorTypes.has(type)) as EConnectorType[];

            if (normalizedConnectors.length > 0) {
                dispatch(setLocationsParamsConnectorTypes(normalizedConnectors));
            }
        }
        if (query.isFree) {
            dispatch(setLocationsParamsIsFree(query.isFree));
        }

        if (query.minPower || query.maxPower) {
            const values = validatePower({
                minPower: query.minPower,
                maxPower: query.maxPower,
            });

            if (values) {
                dispatch(
                    setLocationsParamsPower({
                        minPower: values.minPower,
                        maxPower: values.maxPower,
                    })
                );
            }
        }

        if (query.search) {
            dispatch(setLocationsParamsSearch(query.search));
            setLocationsFilterSearch(query.search);
        }
        if (query.canadianMadeOnly) {
            dispatch(setLocationsParamsCanadianMadeOnly(query.canadianMadeOnly));
        }
    }, [
        query.connectors.length,
        query.connectorStatus,
        query.isFree,
        query.maxPower,
        query.search,
        query.canadianMadeOnly,
    ]);

    return (
        <div css={filtersBlockStyles.root}>
            {isOpen && <FilterMenu onClose={() => setIsOpen(false)} onApply={clickFilterHandler} />}
            <img
                onClick={() => setIsOpen(!isOpen)}
                css={filtersBlockStyles.icon}
                src={filtersIcon}
                alt="filters-icon"
            />
            {quickFilters}
        </div>
    );
};

export default FiltersBlock;
